import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { getMedicalStatement } from '../../../../api/enrollments/get-medical-statement'
import { checkMedicalLevel } from '../../../../api/medical-terms/check-medical-level'
import { useObserver } from '../../../../lib/contexts/observer'
import {
  MedicalConditionEnum,
  medicalConditionEnumMapping
} from '../../../../lib/types/enums/enrollment/medical-condition-enum'
import { enrollmentChildNavbarFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { MedicalStatementProperties } from '../../../../lib/types/models/enrollment/enrollment-medical-statement.properties'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import MedicalStatementActions from '../medical-statement-actions'
import EnrollmentAdditionalMedicalInformationFields from './enrollment-additional-medical-information-fields'
import EnrollmentHealthInformationTranslation from './enrollment-health-information-translation'

type Props = {
  mode: 'create' | 'edit'
  // the backend decides whether LEC may still write the main health field of an existing enrollment
  canEditMainMedicalInformations?: boolean
}

const HealthInformationEnrollmentForm = ({
  mode,
  canEditMainMedicalInformations = false
}: Props) => {
  const [debouncedMedicalInfo, setDebouncedMedicalInfo] = useState('')
  const healthObserver = useObserver({ key: 'enrollment-health-information' })

  const { control, watch, formState, setValue } = useFormContext<EnrollmentInput>()

  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const { medicalLevel, hasChildHealtInformation } = watch()

  const { data: medicalAttestation } = useQuery({
    queryKey: ['medical-attestation', Number(id)],
    queryFn: () => {
      if (id) return getMedicalStatement(Number(id))
    },
    enabled: !!id
  })

  const { data: checkMedicalLevelStatus } = useQuery({
    queryKey: ['check-medical-level'],
    queryFn: () => checkMedicalLevel,
    enabled: !!debouncedMedicalInfo
  })

  const showMedicalInformation =
    mode === 'edit' &&
    hasChildHealtInformation === true &&
    !!formState.defaultValues?.medicalInformations

  const medicalStatus = watch('medicalStatus')

  // a new enrollment is a phone enrollment typed by LEC, so its main health field is always open
  const canWriteMainMedicalInformations = mode === 'create' || canEditMainMedicalInformations

  useEffect(() => {
    const handler = setTimeout(async () => {
      const defaultMedicalStatus = watch('medicalLevel')
      if (checkMedicalLevelStatus && debouncedMedicalInfo) {
        const level = await checkMedicalLevelStatus({ text: debouncedMedicalInfo })
        setValue('medicalLevel', level.result ?? defaultMedicalStatus)
      }

      if (!debouncedMedicalInfo) setValue('medicalLevel', defaultMedicalStatus)
    }, 500) // Adjust debounce delay as needed

    return () => clearTimeout(handler)
  }, [debouncedMedicalInfo])

  useEffect(() => {
    if (!hasChildHealtInformation) {
      setValue('medicalLevel', MedicalConditionEnum.RAS)
    } else if (medicalLevel === MedicalConditionEnum.RAS)
      setValue('medicalLevel', MedicalConditionEnum.LEVEL_1)
  }, [hasChildHealtInformation])

  return (
    <InnerBox className="rounded-2xl">
      <div className="flex items-center gap-2">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          Informations sur la santé
        </Typography>
        {mode !== 'create' &&
          !!formState.defaultValues?.medicalInformations &&
          id &&
          !isNaN(Number(id)) && (
            <MedicalStatementActions
              enrollmentId={Number(id)}
              medicalStatus={medicalStatus}
              additionalMedicalInformations={
                formState.defaultValues?.additionalMedicalInformations
              }
              additionalMedicalInformationsEn={
                formState.defaultValues?.additionalMedicalInformationsEn
              }
              onSuccess={() => {
                queryClient.invalidateQueries({
                  queryKey: ['enrollment', id, enrollmentChildNavbarFields]
                })
                healthObserver.notify(['enrollment-form-update'])
              }}
            />
          )}
      </div>

      {mode === 'create' || !showMedicalInformation ? (
        <div className="flex flex-col gap-3">
          <div className="h-3" />
          <Typography variant="label-semibold">
            Avez-vous une information particulière à nous communiquer sur la santé de votre enfant
            ?
          </Typography>
          <div className="flex gap-10">
            <FormField
              control={control}
              name="hasChildHealtInformation"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledRadioInput
                      {...field}
                      label="Oui"
                      checked={field.value === true}
                      onCheck={() => field.onChange(true)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="hasChildHealtInformation"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledRadioInput
                      {...field}
                      label="Non"
                      checked={field.value === false}
                      onCheck={() => field.onChange(false)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex gap-2 items-center">
            <Typography variant="label-medium">Niveau médical</Typography>

            <FormField
              control={control}
              name="medicalLevel"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      mode="badge"
                      variantColor={
                        medicalLevel
                          ? medicalConditionEnumMapping[medicalLevel]?.variant
                          : undefined
                      }
                      options={Object.values(MedicalConditionEnum).map((value) => ({
                        label: medicalConditionEnumMapping[value].label,
                        value
                      }))}
                      label={
                        medicalLevel ? medicalConditionEnumMapping[medicalLevel]?.label : undefined
                      }
                      disabled={!hasChildHealtInformation}
                      selectedOption={getSelectedEnumOption(
                        field.value,
                        medicalConditionEnumMapping
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          {hasChildHealtInformation && !canWriteMainMedicalInformations && (
            <EnrollmentAdditionalMedicalInformationFields />
          )}
          {hasChildHealtInformation && canWriteMainMedicalInformations && (
            <FormField
              control={control}
              name="medicalInformations"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="textarea"
                      label="Information sur la santé"
                      onChange={(event) => {
                        const value =
                          typeof event === 'string' ? event : event?.target?.value || ''
                        field.onChange(value)
                        setDebouncedMedicalInfo(value)
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
        </div>
      ) : (
        <div className="mt-2">
          <div className="flex gap-1 flex-col">
            {medicalStatus &&
              MedicalStatementProperties.getMedicalAttestationBadges({
                medicalAttestation,
                medicalStatus
              })?.map((badge, index) => (
                <Badge
                  key={index}
                  variant={badge.variant}
                  text={badge.label}
                  icon={badge.icon ? <badge.icon /> : undefined}
                  iconPosition="start"
                />
              ))}
          </div>
          <div className="h-3" />
          <EnrollmentHealthInformationTranslation
            canEditMainMedicalInformations={canEditMainMedicalInformations}
          />
        </div>
      )}
    </InnerBox>
  )
}

export default HealthInformationEnrollmentForm
